/**
 * executeInverse - Executes inverse operations for undo
 *
 * Maps inverse operation data to API calls that revert mutations.
 */

import type {
	InverseOperationData,
	CreateBlockInverseData,
	UpdateBlockInverseData,
	DeleteBlockInverseData,
	CreateReferenceInverseData,
	DeleteReferenceInverseData,
	MoveBlockInverseData,
	ReorderBlocksInverseData,
	ApplyFormationInverseData,
	RemoveFormationInverseData,
	ApplyConceptInverseData,
} from './Types'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'


/**
 * Executes the inverse of an operation to undo it.
 * @param inverseData - Data describing how to undo the operation
 * @throws Error if the API call fails
 */
export async function executeInverse(inverseData: InverseOperationData): Promise<void> {
	switch (inverseData.type) {
		case 'create_block':
			await executeCreateBlockInverse(inverseData)
			break
		case 'update_block':
			await executeUpdateBlockInverse(inverseData)
			break
		case 'delete_block':
			await executeDeleteBlockInverse(inverseData)
			break
		case 'create_reference':
			await executeCreateReferenceInverse(inverseData)
			break
		case 'delete_reference':
			await executeDeleteReferenceInverse(inverseData)
			break
		case 'move_block':
			await executeMoveBlockInverse(inverseData)
			break
		case 'reorder_blocks':
			await executeReorderBlocksInverse(inverseData)
			break
		case 'apply_formation':
			await executeApplyFormationInverse(inverseData)
			break
		case 'remove_formation':
			await executeRemoveFormationInverse(inverseData)
			break
		case 'apply_concept':
			await executeApplyConceptInverse(inverseData)
			break
		default: {
			// TypeScript exhaustiveness check
			const _exhaustive: never = inverseData
			throw new Error(`Unknown inverse operation type: ${(_exhaustive as InverseOperationData).type}`)
		}
	}
}

/**
 * Inverse of create_block: Delete the block that was created.
 */
async function executeCreateBlockInverse(data: CreateBlockInverseData): Promise<void> {
	const response = await secureFetch(`/api/blocks/${data.blockId}/delete`, {
		method: 'DELETE',
	})

	if (!response.ok) {
		throw new Error(`Failed to delete block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Inverse of update_block: Restore original values.
 */
async function executeUpdateBlockInverse(data: UpdateBlockInverseData): Promise<void> {
	const response = await secureJsonFetch(`/api/blocks/${data.blockId}/update`, {
		method: 'PATCH',
		body: data.originalValues,
	})

	if (!response.ok) {
		throw new Error(`Failed to restore block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Inverse of delete_block: Recreate the block with its children and references.
 */
async function executeDeleteBlockInverse(data: DeleteBlockInverseData): Promise<void> {
	// First, recreate the parent block
	const response = await secureJsonFetch('/api/blocks', {
		method: 'POST',
		body: data.block,
	})

	if (!response.ok) {
		throw new Error(`Failed to recreate block: ${response.statusText}`)
	}

	// Then recreate children in order (they have the correct parent_block_id)
	for (const child of data.children) {
		const childResponse = await secureJsonFetch('/api/blocks', {
			method: 'POST',
			body: child,
		})

		if (!childResponse.ok) {
			throw new Error(`Failed to recreate child block: ${childResponse.statusText}`)
		}
	}

	// Finally, recreate references
	for (const ref of data.references) {
		const refResponse = await secureJsonFetch('/api/references', {
			method: 'POST',
			body: {
				source_block_id: ref.source_block_id,
				target_block_id: ref.target_block_id,
				reference_type: ref.reference_type,
				display_options: ref.display_options,
				display_order: ref.display_order,
			},
		})

		if (!refResponse.ok) {
			throw new Error(`Failed to recreate reference: ${refResponse.statusText}`)
		}
	}
}

/**
 * Inverse of create_reference: Delete the reference that was created.
 */
async function executeCreateReferenceInverse(data: CreateReferenceInverseData): Promise<void> {
	const response = await secureFetch(`/api/references/${data.referenceId}`, {
		method: 'DELETE',
	})

	if (!response.ok) {
		throw new Error(`Failed to delete reference ${data.referenceId}: ${response.statusText}`)
	}
}

/**
 * Inverse of delete_reference: Recreate the reference.
 */
async function executeDeleteReferenceInverse(data: DeleteReferenceInverseData): Promise<void> {
	const response = await secureJsonFetch('/api/references', {
		method: 'POST',
		body: {
			source_block_id: data.sourceBlockId,
			target_block_id: data.targetBlockId,
			reference_type: data.referenceType,
			display_options: data.displayOptions,
			display_order: data.displayOrder,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to recreate reference: ${response.statusText}`)
	}
}

/**
 * Inverse of move_block: Move block back to original position.
 */
async function executeMoveBlockInverse(data: MoveBlockInverseData): Promise<void> {
	const response = await secureJsonFetch(`/api/blocks/${data.blockId}/move`, {
		method: 'PATCH',
		body: {
			parent_block_id: data.originalParentBlockId,
			display_order: data.originalDisplayOrder,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to move block ${data.blockId}: ${response.statusText}`)
	}
}

/**
 * Inverse of reorder_blocks: Restore original display orders.
 */
async function executeReorderBlocksInverse(data: ReorderBlocksInverseData): Promise<void> {
	// Convert Map to array of updates
	const updates = Array.from(data.originalOrders.entries()).map(([blockId, displayOrder]) => ({
		block_id: blockId,
		display_order: displayOrder,
	}))

	const response = await secureJsonFetch('/api/blocks/reorder', {
		method: 'PATCH',
		body: {
			parent_block_id: data.parentBlockId,
			updates,
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to reorder blocks: ${response.statusText}`)
	}
}

/**
 * Inverse of apply_formation/remove_formation: Apply or remove formation.
 * If formationBlockId is null/undefined, removes formation; otherwise applies it.
 */
async function executeApplyFormationInverse(data: ApplyFormationInverseData): Promise<void> {
	if (data.formationBlockId == null) {
		// Remove the formation
		const response = await secureFetch(`/api/plays/${data.playBlockId}/formation-ref`, {
			method: 'DELETE',
		})

		// 404 is OK - means no formation to remove
		if (!response.ok && response.status !== 404) {
			throw new Error(`Failed to remove formation: ${response.statusText}`)
		}
	} else {
		// Apply the formation
		const response = await secureJsonFetch(`/api/plays/${data.playBlockId}/formation-ref`, {
			method: 'POST',
			body: { formation_block_id: data.formationBlockId },
		})

		if (!response.ok) {
			throw new Error(`Failed to apply formation: ${response.statusText}`)
		}
	}
}

/**
 * Inverse of remove_formation when applied initially: Remove the formation.
 */
async function executeRemoveFormationInverse(data: RemoveFormationInverseData): Promise<void> {
	const response = await secureFetch(`/api/plays/${data.playBlockId}/formation-ref`, {
		method: 'DELETE',
	})

	// 404 is OK - means no formation to remove
	if (!response.ok && response.status !== 404) {
		throw new Error(`Failed to remove formation: ${response.statusText}`)
	}
}

/**
 * Inverse of remove_concept: Re-apply the concept that was removed.
 */
async function executeApplyConceptInverse(data: ApplyConceptInverseData): Promise<void> {
	const response = await secureJsonFetch(`/api/plays/${data.playBlockId}/concept-refs`, {
		method: 'POST',
		body: { concept_block_id: data.conceptBlockId },
	})

	if (!response.ok) {
		throw new Error(`Failed to apply concept: ${response.statusText}`)
	}
}
